import type { getApiReactHooks } from '@frontend/api-client';
import type { TypedFormData as GenericTypedFormData, SecureNoteContent } from '@frontend/api-types';
import type { MutationHandler } from '@frontend/duck-tape';
import type { SecureNoteFormSchema } from '@frontend/forms';
import { getSecureNoteFormDefaultValues, isSecureNoteEmpty, makeSavedFile, useSecureNoteForm } from '@frontend/forms';
import { usePrevious } from '@mantine/hooks';
import { useEffect } from 'react';
import { createSecureNote, updateSecureNote } from '../../secureNotes';
import type { ExtractTaskInitialQuestionWithAnswer } from '../types';

export type UseSecureNoteInitialQuestionProps = {
  formDataClass: typeof GenericTypedFormData;
  handleMutation: MutationHandler;
  initialQuestionWithAnswer: ExtractTaskInitialQuestionWithAnswer<'secure_note'>;
  onChange: (iq: ExtractTaskInitialQuestionWithAnswer<'secure_note'>) => void;
  onSuccess: (iq?: ExtractTaskInitialQuestionWithAnswer<'secure_note'>) => Promise<void>;
  useCreateSecureNoteMutation: ReturnType<typeof getApiReactHooks>['useCreateSecureNoteMutation'];
  useLazyRetrieveSecureNoteContentQuery: ReturnType<typeof getApiReactHooks>['useLazyRetrieveSecureNoteContentQuery'];
  useReplaceSecureNoteContentMutation: ReturnType<typeof getApiReactHooks>['useReplaceSecureNoteContentMutation'];
  useRetrieveClientQuery: ReturnType<typeof getApiReactHooks>['useRetrieveClientQuery'];
};

export const useSecureNoteInitialQuestion = ({
  formDataClass,
  handleMutation,
  initialQuestionWithAnswer,
  onChange,
  onSuccess,
  useCreateSecureNoteMutation,
  useLazyRetrieveSecureNoteContentQuery,
  useReplaceSecureNoteContentMutation,
  useRetrieveClientQuery,
}: UseSecureNoteInitialQuestionProps) => {
  const { data: client } = useRetrieveClientQuery();
  const [retrieveSecureNoteContent, { isLoading: isRetrieveSecureNoteContentLoading }] =
    useLazyRetrieveSecureNoteContentQuery();
  const [createSecureNoteMutation, { isLoading: isCreateSecureNoteLoading }] = useCreateSecureNoteMutation();
  const [updateSecureNoteMutation, { isLoading: isUpdateSecureNoteContentLoading }] =
    useReplaceSecureNoteContentMutation();

  const previousSecureNoteId = usePrevious(initialQuestionWithAnswer.secureNoteId);

  const onSubmit = async (values: SecureNoteFormSchema) => {
    // Update the existing secure note
    if (initialQuestionWithAnswer.secureNoteId) {
      return updateSecureNote({
        data: values,
        formDataClass,
        handleMutation,
        handleMutationOptions: {
          onSuccess: ({ secureNoteId }) => {
            const updatedIQ = { ...initialQuestionWithAnswer, secureNoteId };
            onChange(updatedIQ);
            onSuccess(updatedIQ);
          },
          showSuccessToast: false,
        },
        secureNoteId: initialQuestionWithAnswer.secureNoteId!,
        updateSecureNoteMutation,
      });
    } else {
      // Wants to skip this IQ
      if (isSecureNoteEmpty(values)) {
        onSuccess(initialQuestionWithAnswer);
        return;
      }

      // Create a new secure note
      return createSecureNote({
        client,
        createSecureNoteMutation,
        data: values,
        formDataClass,
        handleMutation,
        handleMutationOptions: {
          onSuccess: ({ secureNoteId }) => {
            const updatedIQ = { ...initialQuestionWithAnswer, secureNoteId };
            onChange(updatedIQ);
            onSuccess(updatedIQ);
          },
          showSuccessToast: false,
        },
        updateSecureNoteMutation,
      });
    }
  };

  const { errors, isValid, resetForm, setFieldValue, submitForm, values } = useSecureNoteForm({
    enableReinitialize: true,
    onSubmit,
  });

  const resetSecureNoteFormValues = (secureNoteContent: SecureNoteContent & { title: string }) => {
    const newValues = getSecureNoteFormDefaultValues({
      attachments: secureNoteContent?.attachments?.map(makeSavedFile) ?? [],
      notes: secureNoteContent?.notes ?? '',
      password: secureNoteContent?.password ?? '',
      title: secureNoteContent?.title ?? '',
      username: secureNoteContent?.username ?? '',
      website: secureNoteContent?.website ?? '',
    });
    resetForm({ values: newValues });
  };

  useEffect(() => {
    (async () => {
      if (previousSecureNoteId !== initialQuestionWithAnswer.secureNoteId) {
        if (initialQuestionWithAnswer.secureNoteId) {
          const secureNoteContent = await retrieveSecureNoteContent(initialQuestionWithAnswer.secureNoteId!).unwrap();
          resetSecureNoteFormValues({ ...secureNoteContent, title: initialQuestionWithAnswer.secureNoteTitle });
        } else {
          resetSecureNoteFormValues({ title: initialQuestionWithAnswer.secureNoteTitle });
        }
      }
    })();
  }, [initialQuestionWithAnswer.secureNoteId]);

  const isLoading = isRetrieveSecureNoteContentLoading || isCreateSecureNoteLoading || isUpdateSecureNoteContentLoading;

  return {
    errors,
    isCreateSecureNoteLoading,
    isLoading,
    isRetrieveSecureNoteContentLoading,
    isUpdateSecureNoteContentLoading,
    isValid,
    setFieldValue,
    submitForm,
    values,
  };
};
